import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import { ArrowLeft, Lock, LockOpen, AlertTriangle, RefreshCw, GitBranch } from 'lucide-react';
import { toast } from 'sonner';
import {
  PageHeader,
  LoadingState,
  DataTable,
  Column,
  KPIGrid,
  MetricCard,
} from '@/components/common';
import { Card, CardContent, CardHeader, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  permitsApi,
  type PermitCaseDetail as CaseDetail,
  type PermitLineItem,
  type RenewalLineage,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { cn, formatDate } from '@/lib/utils';

function Field({ label, value, mono }: { label: string; value?: string | null; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          'mt-0.5 text-sm font-medium',
          mono && 'font-mono',
          !value && 'text-muted-foreground'
        )}
      >
        {value || '—'}
      </dd>
    </div>
  );
}

export default function PermitCaseDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<CaseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [issueOpen, setIssueOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    permitReference: '',
    permitReceivedDate: '',
    permitExpiryDate: '',
  });
  const [lineage, setLineage] = useState<RenewalLineage | null>(null);
  const [renewOpen, setRenewOpen] = useState(false);
  const [renewForm, setRenewForm] = useState({
    newExpiryDate: '',
    reference: '',
    dutyAndTax: '',
    currency: '',
  });

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [caseData, chain] = await Promise.all([
        permitsApi.getCase(id),
        // Lineage is a separate call because a case with no renewals still has
        // a chain of one, and the detail view should not pay for that join.
        permitsApi.getLineage(id).catch(() => null),
      ]);
      setData(caseData);
      setLineage(chain);
    } catch (error) {
      toast.error(getErrorMessage(error));
      navigate('/permits');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [id, load]);

  const recordIssued = async () => {
    if (!id) return;
    try {
      setSaving(true);
      await permitsApi.recordPermitIssued(id, form);
      toast.success('Permit recorded');
      setIssueOpen(false);
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const releaseHold = async () => {
    if (!data) return;
    try {
      setSaving(true);
      await permitsApi.releaseHold(data.complianceAssessment.id);
      toast.success('Hold released. The shipment can now proceed');
      await load();
    } catch (error) {
      // The server refuses until reference, received date and expiry are all
      // recorded; its message names what is missing, so surface it verbatim.
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const renew = async () => {
    if (!id) return;
    try {
      setSaving(true);
      const result = await permitsApi.renew(id, {
        newExpiryDate: renewForm.newExpiryDate,
        reference: renewForm.reference || undefined,
        dutyAndTax: renewForm.dutyAndTax ? Number(renewForm.dutyAndTax) : undefined,
        currency: renewForm.currency || undefined,
      });
      setRenewOpen(false);
      setRenewForm({ newExpiryDate: '', reference: '', dutyAndTax: '', currency: '' });
      if (result.model === 'LINKED_CASE' && result.renewal) {
        toast.success('Renewed as a new linked case');
        navigate(`/permits/${result.renewal.id}`);
      } else {
        toast.success('Prorogation recorded');
        await load();
      }
    } catch (error) {
      // The server refuses a renewal past the regime's cumulative cap and its
      // message names the cap, so surface it rather than a generic failure.
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) return <LoadingState />;

  const held = data.complianceAssessment.isHeld;
  const permitComplete = Boolean(
    data.permitReference && data.permitReceivedDate && data.permitExpiryDate
  );
  const unmatchedLines = data.lineItems.filter((l) => !l.equipmentId).length;

  const lineColumns: Column<PermitLineItem>[] = [
    { key: 'lineNumber', header: '#', sortable: true },
    {
      key: 'description',
      header: 'Description',
      sortable: true,
      render: (row) => (
        <div className="max-w-[280px]">
          <p className="truncate font-medium" title={row.description}>
            {row.description}
          </p>
          {row.serialNumber && (
            <p className="text-xs text-muted-foreground">S/N {row.serialNumber}</p>
          )}
        </div>
      ),
    },
    {
      key: 'assetMatch',
      header: 'Asset',
      accessorFn: (row) => row.equipment?.assetNumber ?? '',
      sortable: true,
      render: (row) =>
        row.equipment ? (
          <span className="font-mono text-xs">{row.equipment.assetNumber}</span>
        ) : (
          // Unmatched lines are flagged, not blocked: consumables and frames
          // may not be registered assets.
          <Badge variant="outline" className="text-xs">
            unmatched
          </Badge>
        ),
    },
    { key: 'hsCode', header: 'HS Code', sortable: true },
    { key: 'quantity', header: 'Qty', sortable: true, className: 'text-right' },
    {
      key: 'totalValue',
      header: 'Value',
      sortable: true,
      className: 'text-right',
      render: (row) =>
        row.totalValue ? `${row.currency ?? ''} ${row.totalValue.toLocaleString()}`.trim() : '—',
    },
  ];

  return (
    <motion.div variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <button
          onClick={() => navigate('/permits')}
          className="mb-2 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Permits
        </button>
        <PageHeader
          title={data.caseNumber}
          description={`${data.caseType.replace(/_/g, ' ')} · invoice ${data.commercialInvoiceRef}`}
          actions={
            <div className="flex gap-2">
              {!permitComplete && (
                <Button onClick={() => setIssueOpen(true)}>Record permit issued</Button>
              )}
              {held && (
                <Button
                  variant={permitComplete ? 'default' : 'outline'}
                  onClick={releaseHold}
                  disabled={saving}
                >
                  <LockOpen className="mr-2 h-4 w-4" />
                  Release hold
                </Button>
              )}
              {/* A licence has nothing to renew: it gates a movement but
                  creates no duty to bring anything back. */}
              {permitComplete && data.caseType !== 'LICENCE' && (
                <Button variant="outline" onClick={() => setRenewOpen(true)}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Renew
                </Button>
              )}
            </div>
          }
        />
      </motion.div>

      {/* The gate, stated plainly. This is what the module is for. */}
      <motion.div variants={fadeUp} className="mt-4">
        <div
          className={cn(
            'flex items-start gap-3 rounded-lg border p-4',
            held
              ? 'border-status-warning-border bg-status-warning-subtle'
              : 'border-status-success-border bg-status-success-subtle'
          )}
        >
          {held ? (
            <Lock className="mt-0.5 h-5 w-5 shrink-0 text-status-warning-foreground" />
          ) : (
            <LockOpen className="mt-0.5 h-5 w-5 shrink-0 text-status-success-foreground" />
          )}
          <div>
            <p className="text-sm font-semibold text-foreground">
              {held ? 'Movement is on hold' : 'Hold released'}
            </p>
            <p className="text-sm text-muted-foreground">
              {held
                ? permitComplete
                  ? 'The permit is recorded. Releasing the hold lets an RFQ be raised for this movement.'
                  : 'No RFQ can be raised until the permit reference, received date and expiry are all recorded.'
                : 'An RFQ can now be raised for this movement.'}
            </p>
          </div>
        </div>
      </motion.div>

      <div className="mt-6">
        <KPIGrid columns={4}>
          <MetricCard
            label="Status"
            value={data.status.replace(/_/g, ' ')}
            sentiment={permitComplete ? 'success' : 'warning'}
          />
          <MetricCard label="Line Items" value={data.lineItems.length} sentiment="info" />
          <MetricCard
            label="Unmatched Lines"
            value={unmatchedLines}
            sentiment={unmatchedLines > 0 ? 'warning' : 'success'}
            subtitle="flagged, not blocked"
          />
          <MetricCard
            label="Country"
            value={data.countryRegime?.countryName ?? '—'}
            sentiment="neutral"
          />
        </KPIGrid>
      </div>

      <motion.div variants={fadeUp} className="mt-6">
        <Card>
          <CardHeader>
            <h3 className="text-base font-semibold text-foreground">Permit</h3>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 lg:grid-cols-4">
              <Field label="Reference" value={data.permitReference} mono />
              <Field
                label="Received"
                value={data.permitReceivedDate ? formatDate(data.permitReceivedDate) : null}
              />
              <Field
                label="Expires"
                value={data.permitExpiryDate ? formatDate(data.permitExpiryDate) : null}
              />
              <Field label="Issuing authority" value={data.issuingAuthority} />
              <Field
                label="Movement"
                value={data.complianceAssessment.movementType.replace(/_/g, ' ')}
              />
              <Field
                label="Route"
                value={`${data.complianceAssessment.transportOrder.originCountry} → ${data.complianceAssessment.transportOrder.destCountry}`}
              />
              <Field
                label="Transport order"
                value={data.complianceAssessment.transportOrder.orderNumber}
                mono
              />
              <Field label="Invoice" value={data.commercialInvoiceRef} mono />
            </dl>
          </CardContent>
        </Card>
      </motion.div>

      {lineage && lineage.chain.length > 1 && (
        <motion.div variants={fadeUp} className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <GitBranch className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-base font-semibold text-foreground">Renewal chain</h3>
              </div>
              <CardDescription>
                {lineage.renewalCount} renewal{lineage.renewalCount === 1 ? '' : 's'} ·{' '}
                {lineage.cumulativeDays} days in country. Each renewal is its own record, so
                cumulative time stays visible against the regime cap.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-center gap-2">
                {lineage.chain.map((link, i) => (
                  <div key={link.id} className="flex items-center gap-2">
                    {i > 0 && <span className="text-muted-foreground">→</span>}
                    <button
                      onClick={() => navigate(`/permits/${link.id}`)}
                      className={cn(
                        'rounded-md border px-2.5 py-1.5 text-left transition-colors',
                        link.id === data.id
                          ? 'border-primary bg-primary/10'
                          : 'border-border bg-card hover:bg-accent'
                      )}
                    >
                      <span className="block font-mono text-xs font-medium">{link.caseNumber}</span>
                      <span className="block text-[11px] text-muted-foreground">
                        {link.permitExpiryDate
                          ? `expires ${formatDate(link.permitExpiryDate)}`
                          : 'no expiry'}
                      </span>
                    </button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
      <motion.div variants={fadeUp} className="mt-6">
        <Card>
          <CardHeader>
            <h3 className="text-base font-semibold text-foreground">Line Items</h3>
            <CardDescription>
              Inherited from the commercial invoice. A case covers exactly one invoice and every
              line on it.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              pagination={false}
              columns={lineColumns}
              data={data.lineItems}
              emptyTitle="No line items yet"
            />
          </CardContent>
        </Card>
      </motion.div>

      <Dialog open={issueOpen} onOpenChange={setIssueOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record permit issued</DialogTitle>
            <DialogDescription>
              All three are required. The hold cannot be released without an expiry date, because a
              blank expiry is how deadlines get missed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="ref">Permit reference</Label>
              <Input
                id="ref"
                value={form.permitReference}
                onChange={(e) => setForm({ ...form, permitReference: e.target.value })}
                placeholder="IQ-TA-2026-5512"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="recv">Received date</Label>
                <Input
                  id="recv"
                  type="date"
                  value={form.permitReceivedDate}
                  onChange={(e) => setForm({ ...form, permitReceivedDate: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="exp">Expiry date</Label>
                <Input
                  id="exp"
                  type="date"
                  value={form.permitExpiryDate}
                  onChange={(e) => setForm({ ...form, permitExpiryDate: e.target.value })}
                />
              </div>
            </div>
            {(!form.permitReference || !form.permitReceivedDate || !form.permitExpiryDate) && (
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <AlertTriangle className="h-3.5 w-3.5" />
                All three fields are needed before the hold can be released.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIssueOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={recordIssued}
              disabled={
                saving ||
                !form.permitReference ||
                !form.permitReceivedDate ||
                !form.permitExpiryDate
              }
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={renewOpen} onOpenChange={setRenewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Renew this admission</DialogTitle>
            <DialogDescription>
              The country's regime decides the shape: a new linked case, or a numbered prorogation
              against this one. Either way the existing record is kept, so cumulative time in
              country stays visible.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="newExpiry">New expiry date</Label>
              <Input
                id="newExpiry"
                type="date"
                value={renewForm.newExpiryDate}
                onChange={(e) => setRenewForm({ ...renewForm, newExpiryDate: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="renewRef">Reference</Label>
              <Input
                id="renewRef"
                value={renewForm.reference}
                onChange={(e) => setRenewForm({ ...renewForm, reference: e.target.value })}
                placeholder="Authorisation or prorogation number"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="duty">Duty and tax</Label>
                <Input
                  id="duty"
                  type="number"
                  value={renewForm.dutyAndTax}
                  onChange={(e) => setRenewForm({ ...renewForm, dutyAndTax: e.target.value })}
                  placeholder="Recorded, not calculated"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="renewCcy">Currency</Label>
                <Input
                  id="renewCcy"
                  value={renewForm.currency}
                  onChange={(e) => setRenewForm({ ...renewForm, currency: e.target.value })}
                  placeholder="DZD"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenewOpen(false)}>
              Cancel
            </Button>
            <Button onClick={renew} disabled={saving || !renewForm.newExpiryDate}>
              Renew
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
